import React, { useState, useEffect } from 'react';
import { View, StyleSheet } from 'react-native';
import { Title, Paragraph, TextInput, Button, Surface, Text } from 'react-native-paper';
import { liquidLayout, liquidCalcLayout, makeCalcAccentStyles } from '../../../theme/liquidLayout';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramBETAirLeakageRate } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { useUnits } from '../../../context/UnitsContext';
import {
  PressureUnit,
  calcAchFromCfm,
  calcCfmPerFt2,
  calcFlowAtPressure,
  formatPressurePair,
  paToInWc,
  pressureToPa,
  validateFlowExponent,
} from '../../../utils/bet';

const ACCENT = '#8b5cf6';
const KEY = 'tabai.calc.betAirLeakageRate.v1';

/**
 * Air Leakage Rate — Q_ref = Q_test × (P_ref / P_test)^n
 * Bidirectional over {Q_test, P_test, P_ref, Q_ref}; n is a parameter.
 * Downstream ACH50 / NLA remain derived displays based on the solved Q_test and its P_test.
 */

type Fields = {
  airflowRate: string;      // Q_test in user's flow units
  testPressure: string;     // P_test in testPressureUnit
  standardPressure: string; // P_ref in standardPressureUnit
  flowExponent: string;     // n
  airLeakageRate: string;   // Q_ref in user's flow units
  buildingVolume: string;
  envelopeArea: string;
  airflowUncertainty: string;
  testPressureUncertainty: string;
  standardPressureUncertainty: string;
  flowExponentUncertainty: string;
};

const EMPTY: Fields = {
  airflowRate: '',
  testPressure: '',
  standardPressure: '',
  flowExponent: '',
  airLeakageRate: '',
  buildingVolume: '',
  envelopeArea: '',
  airflowUncertainty: '',
  testPressureUncertainty: '',
  standardPressureUncertainty: '',
  flowExponentUncertainty: '',
};

const BETAirLeakageRateScreen = () => {
  const { unit, toImp, fromImp } = useUnits();
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);

  const [testPressureUnit, setTestPressureUnit] = useState<PressureUnit>('Pa');
  const [standardPressureUnit, setStandardPressureUnit] = useState<PressureUnit>('Pa');

  // Derived downstream displays
  const [qAt50Pa, setQAt50Pa] = useState('');
  const [qAt75Pa, setQAt75Pa] = useState('');
  const [qAt030InWc, setQAt030InWc] = useState('');
  const [ach50, setAch50] = useState('');
  const [cfm50PerFt2, setCfm50PerFt2] = useState('');
  const [cfm030PerFt2, setCfm030PerFt2] = useState('');

  const [combinedUncertainty, setCombinedUncertainty] = useState<number | null>(null);
  const [expandedUncertainty, setExpandedUncertainty] = useState<number | null>(null);

  const set = (patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch }));

  const primaryFilledCount = () => {
    return [fields.airflowRate, fields.testPressure, fields.standardPressure, fields.airLeakageRate]
      .filter((v) => v !== '').length;
  };

  const calculate = () => {
    setError(null);

    // n is required parameter
    if (!fields.flowExponent) {
      setError('Flow Exponent (n) is required.');
      return;
    }
    const n = parseFloat(fields.flowExponent);
    if (!Number.isFinite(n)) { setError('Flow Exponent must be numeric.'); return; }
    const nValidation = validateFlowExponent(n);
    if (!nValidation.ok) { setError(nValidation.error); return; }

    const filled = primaryFilledCount();
    if (filled < 3) {
      setError('Fill any 3 of {Q_test, P_test, P_ref, Q_ref} to solve the 4th.');
      return;
    }
    if (filled === 4) {
      setError('All 4 primary values are filled — clear one to solve for it.');
      return;
    }

    const qTest = fields.airflowRate ? toImp(fields.airflowRate, 'flow') : NaN;
    const pTestInput = fields.testPressure ? parseFloat(fields.testPressure) : NaN;
    const pStdInput = fields.standardPressure ? parseFloat(fields.standardPressure) : NaN;
    const qRef = fields.airLeakageRate ? toImp(fields.airLeakageRate, 'flow') : NaN;

    const pTestPa = Number.isFinite(pTestInput) ? pressureToPa(pTestInput, testPressureUnit) : NaN;
    const pStdPa = Number.isFinite(pStdInput) ? pressureToPa(pStdInput, standardPressureUnit) : NaN;

    let solvedQTestImp = qTest;
    let solvedPTestPa = pTestPa;

    if (fields.airLeakageRate === '') {
      if (!(qTest > 0) || !(pTestPa > 0) || !(pStdPa > 0)) { setError('Q_test, P_test, P_ref must be > 0.'); return; }
      const qRefSolved = calcFlowAtPressure(qTest, pTestPa, pStdPa, n);
      setSolvedFor('airLeakageRate');
      set({ airLeakageRate: fromImp(qRefSolved, 'flow', 2) });
    } else if (fields.airflowRate === '') {
      if (!(qRef > 0) || !(pTestPa > 0) || !(pStdPa > 0)) { setError('Q_ref, P_test, P_ref must be > 0.'); return; }
      // Q_test = Q_ref / (P_ref/P_test)^n
      const qTestSolved = qRef / Math.pow(pStdPa / pTestPa, n);
      solvedQTestImp = qTestSolved;
      setSolvedFor('airflowRate');
      set({ airflowRate: fromImp(qTestSolved, 'flow', 2) });
    } else if (fields.testPressure === '') {
      if (!(qTest > 0) || !(qRef > 0) || !(pStdPa > 0)) { setError('Q_test, Q_ref, P_ref must be > 0.'); return; }
      // P_test = P_ref × (Q_test/Q_ref)^(1/n)
      const pTestSolvedPa = pStdPa * Math.pow(qTest / qRef, 1 / n);
      solvedPTestPa = pTestSolvedPa;
      const pTestSolvedUser = testPressureUnit === 'Pa' ? pTestSolvedPa : paToInWc(pTestSolvedPa);
      setSolvedFor('testPressure');
      set({ testPressure: pTestSolvedUser.toFixed(testPressureUnit === 'Pa' ? 2 : 4) });
    } else if (fields.standardPressure === '') {
      if (!(qTest > 0) || !(qRef > 0) || !(pTestPa > 0)) { setError('Q_test, Q_ref, P_test must be > 0.'); return; }
      // P_ref = P_test × (Q_ref/Q_test)^(1/n)
      const pStdSolvedPa = pTestPa * Math.pow(qRef / qTest, 1 / n);
      const pStdSolvedUser = standardPressureUnit === 'Pa' ? pStdSolvedPa : paToInWc(pStdSolvedPa);
      setSolvedFor('standardPressure');
      set({ standardPressure: pStdSolvedUser.toFixed(standardPressureUnit === 'Pa' ? 2 : 4) });
    }

    // Downstream derived values — use solvedQTest/solvedPTest when available
    if (solvedQTestImp > 0 && solvedPTestPa > 0) {
      const p030InWcPa = pressureToPa(0.3, 'in_w_c');
      const q50 = calcFlowAtPressure(solvedQTestImp, solvedPTestPa, 50, n);
      const q75 = calcFlowAtPressure(solvedQTestImp, solvedPTestPa, 75, n);
      const q030 = calcFlowAtPressure(solvedQTestImp, solvedPTestPa, p030InWcPa, n);
      setQAt50Pa(fromImp(q50, 'flow', 2));
      setQAt75Pa(fromImp(q75, 'flow', 2));
      setQAt030InWc(fromImp(q030, 'flow', 2));

      const volumeFt3 = fields.buildingVolume ? toImp(fields.buildingVolume, 'volume') : NaN;
      if (Number.isFinite(volumeFt3) && volumeFt3 > 0) {
        setAch50(calcAchFromCfm(volumeFt3, q50).toFixed(2));
      } else {
        setAch50('');
      }
      const areaFt2 = fields.envelopeArea ? toImp(fields.envelopeArea, 'area') : NaN;
      if (Number.isFinite(areaFt2) && areaFt2 > 0) {
        setCfm50PerFt2(calcCfmPerFt2(q50, areaFt2).toFixed(3));
        setCfm030PerFt2(calcCfmPerFt2(q030, areaFt2).toFixed(3));
      } else {
        setCfm50PerFt2('');
        setCfm030PerFt2('');
      }
    }
  };

  useEffect(() => {
    // Uncertainty display — only when all primaries + n available
    if (!fields.airflowRate || !fields.testPressure || !fields.standardPressure || !fields.flowExponent || !fields.airLeakageRate) {
      setCombinedUncertainty(null);
      setExpandedUncertainty(null);
      return;
    }
    const qTest = toImp(fields.airflowRate, 'flow');
    const pTestInput = parseFloat(fields.testPressure);
    const pStdInput = parseFloat(fields.standardPressure);
    const n = parseFloat(fields.flowExponent);
    const qStandard = toImp(fields.airLeakageRate, 'flow');
    const pTest = pressureToPa(pTestInput, testPressureUnit);
    const pStandard = pressureToPa(pStdInput, standardPressureUnit);
    if (!(qTest > 0) || !(pTest > 0) || !(pStandard > 0) || !Number.isFinite(n) || !(qStandard > 0)) {
      setCombinedUncertainty(null);
      setExpandedUncertainty(null);
      return;
    }
    const u_Q_test = fields.airflowUncertainty ? toImp(fields.airflowUncertainty, 'flow') : qTest * 0.03;
    const u_p_test = fields.testPressureUncertainty ? parseFloat(fields.testPressureUncertainty) : pTest * 0.02;
    const u_p_standard = fields.standardPressureUncertainty ? parseFloat(fields.standardPressureUncertainty) : pStandard * 0.02;
    const u_n = fields.flowExponentUncertainty ? parseFloat(fields.flowExponentUncertainty) : 0.05;
    const rQ = Math.pow(u_Q_test / qTest, 2);
    const rPT = Math.pow((n * u_p_test) / pTest, 2);
    const rPS = Math.pow((n * u_p_standard) / pStandard, 2);
    const rN = Math.pow(Math.log(pStandard / pTest) * u_n, 2);
    const combined = qStandard * Math.sqrt(rQ + rPT + rPS + rN);
    setCombinedUncertainty(combined);
    setExpandedUncertainty(combined * 2);
  }, [fields, testPressureUnit, standardPressureUnit, toImp]);

  const clearAll = () => {
    setError(null);
    setTestPressureUnit('Pa');
    setStandardPressureUnit('Pa');
    setQAt50Pa('');
    setQAt75Pa('');
    setQAt030InWc('');
    setAch50('');
    setCfm50PerFt2('');
    setCfm030PerFt2('');
    setCombinedUncertainty(null);
    setExpandedUncertainty(null);
    setSolvedFor(null);
    void clearPersisted();
  };

  const result = [
    fields.airflowRate && { label: 'Q_test', value: fields.airflowRate, unit: unit('flow'), emphasis: solvedFor === 'airflowRate' },
    fields.testPressure && { label: 'P_test', value: fields.testPressure, unit: testPressureUnit === 'Pa' ? 'Pa' : 'in. w.c.', emphasis: solvedFor === 'testPressure' },
    fields.standardPressure && { label: 'P_ref', value: fields.standardPressure, unit: standardPressureUnit === 'Pa' ? 'Pa' : 'in. w.c.', emphasis: solvedFor === 'standardPressure' },
    fields.flowExponent && { label: 'n', value: fields.flowExponent },
    fields.airLeakageRate && { label: 'Q_ref', value: fields.airLeakageRate, unit: unit('flow'), emphasis: solvedFor === 'airLeakageRate' },
  ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[];

  return (
    <CalcShell
      title="Air Leakage Rate"
      subtitle="Solve for Q_test, P_test, P_ref, or Q_ref using the power-law leakage relation — fill any 3 of 4 (plus n)"
      formula={
        <>
          <FormulaLine>Q_ref = Q_test × (P_ref / P_test)^n</FormulaLine>
          <FormulaNote>n typically 0.5–0.7. Common standards reference 0.30 in. w.c. (≈ 75 Pa).</FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      copyFooter="Formula: Q_ref = Q_test × (P_ref / P_test)^n"
      diagram={<DiagramBETAirLeakageRate />}
      outputSlots={{
        cfm: fields.airLeakageRate ? toImp(fields.airLeakageRate, 'flow') : (fields.airflowRate ? toImp(fields.airflowRate, 'flow') : undefined),
        volume_cuft: fields.buildingVolume ? toImp(fields.buildingVolume, 'volume') : undefined,
        air_changes_per_hour: ach50 ? parseFloat(ach50) : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.cfm != null && !fields.airflowRate) patch.airflowRate = fromImp(values.cfm, 'flow', 2);
        if (values.volume_cuft != null && !fields.buildingVolume) patch.buildingVolume = fromImp(values.volume_cuft, 'volume', 2);
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <View style={{ marginBottom: 12 }}>
        <NumericField label={`Q_test — Airflow Rate at Test (${unit('flow')})`} value={fields.airflowRate} onChangeText={(v) => set({ airflowRate: v })} rightText={unit('flow')} maxDecimals={2} onSubmitEditing={calculate} />
      </View>

      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`P_test — Test Pressure (${testPressureUnit === 'Pa' ? 'Pa' : 'in. w.c.'})`}
          value={fields.testPressure}
          onChangeText={(v) => set({ testPressure: v })}
          rightText={testPressureUnit === 'Pa' ? 'Pa' : 'in. w.c.'}
          maxDecimals={4}
          onSubmitEditing={calculate}
        />
        <View style={styles.unitToggleRow}>
          <Button mode={testPressureUnit === 'Pa' ? 'contained' : 'outlined'} compact onPress={() => setTestPressureUnit('Pa')} style={styles.unitToggleButton}>Pa</Button>
          <Button mode={testPressureUnit === 'in_w_c' ? 'contained' : 'outlined'} compact onPress={() => setTestPressureUnit('in_w_c')} style={styles.unitToggleButton}>in. w.c.</Button>
        </View>
        {!!fields.testPressure && Number.isFinite(parseFloat(fields.testPressure)) && (
          <Paragraph style={styles.unitHint}>
            {testPressureUnit === 'Pa'
              ? `≈ ${paToInWc(parseFloat(fields.testPressure)).toFixed(3)} in. w.c.`
              : `≈ ${pressureToPa(parseFloat(fields.testPressure), 'in_w_c').toFixed(1)} Pa`}
          </Paragraph>
        )}
      </View>

      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`P_ref — Standard / Reference Pressure (${standardPressureUnit === 'Pa' ? 'Pa' : 'in. w.c.'})`}
          value={fields.standardPressure}
          onChangeText={(v) => set({ standardPressure: v })}
          rightText={standardPressureUnit === 'Pa' ? 'Pa' : 'in. w.c.'}
          maxDecimals={4}
          onSubmitEditing={calculate}
        />
        <View style={styles.unitToggleRow}>
          <Button mode={standardPressureUnit === 'Pa' ? 'contained' : 'outlined'} compact onPress={() => setStandardPressureUnit('Pa')} style={styles.unitToggleButton}>Pa</Button>
          <Button mode={standardPressureUnit === 'in_w_c' ? 'contained' : 'outlined'} compact onPress={() => setStandardPressureUnit('in_w_c')} style={styles.unitToggleButton}>in. w.c.</Button>
        </View>
        {!!fields.standardPressure && Number.isFinite(parseFloat(fields.standardPressure)) && (
          <Paragraph style={styles.unitHint}>
            {standardPressureUnit === 'Pa'
              ? `≈ ${paToInWc(parseFloat(fields.standardPressure)).toFixed(3)} in. w.c.`
              : `≈ ${pressureToPa(parseFloat(fields.standardPressure), 'in_w_c').toFixed(1)} Pa`}
          </Paragraph>
        )}
      </View>

      <View style={{ marginBottom: 12 }}>
        <NumericField label="Flow Exponent (n)" value={fields.flowExponent} onChangeText={(v) => set({ flowExponent: v })} maxDecimals={3} onSubmitEditing={calculate} />
      </View>

      <View style={{ marginBottom: 12 }}>
        <NumericField label={`Q_ref — Air Leakage Rate at Reference (${unit('flow')})`} value={fields.airLeakageRate} onChangeText={(v) => set({ airLeakageRate: v })} rightText={unit('flow')} maxDecimals={2} onSubmitEditing={calculate} />
      </View>

      <Surface style={styles.subCard}>
        <Title style={styles.subCardTitle}>Optional Normalization</Title>
        <Paragraph style={styles.subCardHint}>Enter building volume and/or envelope area to compute ACH50 and CFM50/ft².</Paragraph>
        <View style={{ marginBottom: 12 }}>
          <NumericField label={`Building Volume (${unit('volume')})`} value={fields.buildingVolume} onChangeText={(v) => set({ buildingVolume: v })} rightText={unit('volume')} maxDecimals={2} />
        </View>
        <View style={{ marginBottom: 12 }}>
          <NumericField label={`Envelope / Exterior Surface Area (${unit('area')})`} value={fields.envelopeArea} onChangeText={(v) => set({ envelopeArea: v })} rightText={unit('area')} maxDecimals={2} />
        </View>
      </Surface>

      <Surface style={styles.subCard}>
        <Title style={styles.subCardTitle}>Measurement Uncertainty</Title>
        <Paragraph style={styles.subCardHint}>Leave blank for default estimates (3% flow, 2% pressure, 0.05 for n).</Paragraph>
        <View style={{ marginBottom: 12 }}>
          <NumericField label={`Airflow Uncertainty (${unit('flow')})`} value={fields.airflowUncertainty} onChangeText={(v) => set({ airflowUncertainty: v })} rightText={unit('flow')} maxDecimals={2} />
        </View>
        <View style={{ marginBottom: 12 }}>
          <NumericField label="Test Pressure Uncertainty (Pa)" value={fields.testPressureUncertainty} onChangeText={(v) => set({ testPressureUncertainty: v })} rightText="Pa" maxDecimals={2} />
        </View>
        <View style={{ marginBottom: 12 }}>
          <NumericField label="Standard Pressure Uncertainty (Pa)" value={fields.standardPressureUncertainty} onChangeText={(v) => set({ standardPressureUncertainty: v })} rightText="Pa" maxDecimals={2} />
        </View>
        <View style={{ marginBottom: 12 }}>
          <NumericField label="Flow Exponent Uncertainty" value={fields.flowExponentUncertainty} onChangeText={(v) => set({ flowExponentUncertainty: v })} maxDecimals={3} />
        </View>
      </Surface>

      {!!fields.airLeakageRate && (
        <Surface style={styles.resultCard}>
          <Text style={styles.resultLabel}>Derived References</Text>
          <Text style={styles.resultValue}>
            Q_ref: {fields.airLeakageRate} {unit('flow')}
            {' '}({(() => {
              const pStdInput = parseFloat(fields.standardPressure || '75');
              const pStdPa = Number.isFinite(pStdInput) ? pressureToPa(pStdInput, standardPressureUnit) : 75;
              const pair = formatPressurePair(pStdPa);
              return `${pair.inWc} / ${pair.pa}`;
            })()})
          </Text>
          {!!qAt030InWc && <Text style={styles.resultValue}>CFM @ 0.30 in. w.c.: {qAt030InWc}</Text>}
          {!!qAt50Pa && <Text style={styles.resultValue}>CFM50 (Q₅₀): {qAt50Pa}</Text>}
          {!!qAt75Pa && <Text style={styles.resultValue}>CFM75 (Q₇₅): {qAt75Pa}</Text>}
          {!!ach50 && <Text style={styles.resultValue}>ACH50: {ach50} 1/hr</Text>}
          {!!cfm50PerFt2 && <Text style={styles.resultValue}>CFM50/ft²: {cfm50PerFt2}</Text>}
          {!!cfm030PerFt2 && <Text style={styles.resultValue}>CFM @ 0.30 in. w.c. per ft²: {cfm030PerFt2}</Text>}
        </Surface>
      )}

      {combinedUncertainty !== null && (
        <Surface style={styles.resultCard}>
          <Text style={styles.resultLabel}>Uncertainty Analysis</Text>
          <Text style={styles.resultValue}>Combined Uncertainty: ±{fromImp(combinedUncertainty, 'flow', 2)} {unit('flow')}</Text>
          <Text style={styles.resultValue}>
            Relative Uncertainty: ±{((combinedUncertainty / toImp(fields.airLeakageRate, 'flow')) * 100).toFixed(2)}%
          </Text>
          {expandedUncertainty !== null && (
            <>
              <Text style={styles.resultValue}>
                Expanded Uncertainty (k=2, 95%): ±{fromImp(expandedUncertainty, 'flow', 2)} {unit('flow')}
              </Text>
              <Text style={styles.resultValue}>
                Q_ref: {fields.airLeakageRate} ± {fromImp(expandedUncertainty, 'flow', 2)} {unit('flow')} (95% CI)
              </Text>
            </>
          )}
        </Surface>
      )}
    </CalcShell>
  );
};

const styles = StyleSheet.create({
  ...liquidLayout,
  ...liquidCalcLayout,
  ...makeCalcAccentStyles(ACCENT),

  unitHint: {
    marginTop: 6,
    fontSize: 12,
    color: '#FFFFFF',
    opacity: 0.6,
  },
  unitToggleRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 8,
  },
  unitToggleButton: {
    flex: 1,
  },
  subCard: {
    padding: 16,
    borderRadius: 12,
    marginTop: 12,
    backgroundColor: ACCENT + '14',
    borderWidth: 1,
    borderColor: ACCENT + '33',
  },
  subCardTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: ACCENT,
    marginBottom: 6,
  },
  subCardHint: {
    fontSize: 12,
    opacity: 0.75,
    marginBottom: 12,
  },
  resultCard: {
    padding: 16,
    borderRadius: 12,
    marginTop: 12,
    backgroundColor: ACCENT + '26',
    borderWidth: 1,
    borderColor: ACCENT + '4D',
  },
  resultLabel: {
    fontSize: 16,
    fontWeight: 'bold',
    color: ACCENT,
    marginBottom: 8,
  },
  resultValue: {
    fontSize: 15,
    marginBottom: 4,
    fontWeight: '500',
  },
});

export default BETAirLeakageRateScreen;
